import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  FileWarning,
  FileText,
  Building2,
  MapPin,
  Calendar,
  Clock,
  Loader2,
  ExternalLink,
  Receipt,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Configuration visuelle du bandeau selon document_kind retourné par l'API.
 * Aligne strictement la sémantique du document avec le titre PDF dynamique :
 *   devis_draft            → Précontractuel, non engageant
 *   devis_signed_facture   → Document valant facture (Bon pour accord souscripteur, en attente paiement)
 *   devis_signed_acompte   → Facture d'acompte (souscripteur signé, acompte versé)
 *   attestation_active     → Attestation officielle DOMIUM (signée Présidence + acquittée → garantie active)
 */
const KIND_CONFIG = {
  devis_draft: {
    testId: 'verify-status-draft',
    badge: 'Devis ESS — Précontractuel',
    title: 'Document non signé, non engageant',
    description:
      "Cet aperçu correspond à un Devis ESS DOS-DOMIUM non signé. Il ne vaut pas facture, n'engage pas COPPAM et ne tient pas lieu de garantie. Seule la version signée par le souscripteur puis acquittée fait courir une garantie.",
    Icon: FileText,
    iconBg: 'bg-amber-500',
    iconShadow: 'shadow-amber-200',
    cardBorder: 'border-amber-300',
    cardBg: 'bg-gradient-to-br from-amber-50 to-orange-50',
    badgeBg: 'bg-amber-500',
    headerColor: 'text-amber-950',
    bodyColor: 'text-amber-900/80',
  },
  devis_signed_facture: {
    testId: 'verify-status-devis-facture',
    badge: 'Document valant facture',
    title: 'Bon pour accord du souscripteur — En attente d\'acquittement',
    description:
      "Le souscripteur a apposé son « Bon pour accord » sur l'appel à contribution coopérative. Ce document vaut facture pour le règlement, mais la garantie DOMIUM ne devient active qu'après acquittement complet et contre-signature de la Présidence COPPAM.",
    Icon: Receipt,
    iconBg: 'bg-sky-600',
    iconShadow: 'shadow-sky-200',
    cardBorder: 'border-sky-300',
    cardBg: 'bg-gradient-to-br from-sky-50 to-blue-50',
    badgeBg: 'bg-sky-600',
    headerColor: 'text-sky-950',
    bodyColor: 'text-sky-900/80',
  },
  devis_signed_acompte: {
    testId: 'verify-status-devis-acompte',
    badge: 'Facture d\'acompte / Provision',
    title: 'Bon pour accord du souscripteur — Acompte versé',
    description:
      "Un acompte (provision) a été perçu sur l'appel à contribution. La garantie DOMIUM reste suspendue à l'acquittement total et à la contre-signature de la Présidence COPPAM.",
    Icon: Receipt,
    iconBg: 'bg-indigo-600',
    iconShadow: 'shadow-indigo-200',
    cardBorder: 'border-indigo-300',
    cardBg: 'bg-gradient-to-br from-indigo-50 to-violet-50',
    badgeBg: 'bg-indigo-600',
    headerColor: 'text-indigo-950',
    bodyColor: 'text-indigo-900/80',
  },
  attestation_active: {
    testId: 'verify-status-active',
    badge: "Attestation nominative officielle DOS DOMIUM Dommages à l'ouvrage",
    title: 'Document contractuel actif',
    description:
      "Cette attestation nominative DOS DOMIUM Dommages à l'ouvrage est signée par la Présidence COPPAM, acquittée et juridiquement engageante. La garantie solidaire DOS-DOMIUM est en vigueur.",
    Icon: ShieldCheck,
    iconBg: 'bg-emerald-600',
    iconShadow: 'shadow-emerald-200',
    cardBorder: 'border-emerald-300',
    cardBg: 'bg-gradient-to-br from-emerald-50 to-green-50',
    badgeBg: 'bg-emerald-600',
    headerColor: 'text-emerald-950',
    bodyColor: 'text-emerald-900/80',
  },
};

function StatusBanner({ data }) {
  if (!data.found || data.status === 'NOT_FOUND') {
    return (
      <div
        data-testid="verify-status-not-found"
        className="rounded-2xl border-2 border-gray-300 bg-gray-50 p-6 md:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-gray-200 p-3">
            <FileWarning className="h-8 w-8 text-gray-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
              Document introuvable
            </h2>
            <p className="mt-2 text-gray-700 text-base">
              Aucun document DOS-DOMIUM ne correspond à la référence{' '}
              <span className="font-mono font-semibold">{data.reference}</span>.
              Vérifiez la référence ou contactez COPPAM.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Choix du bandeau selon document_kind (fallback sur is_active si absent pour compat)
  const kind = data.document_kind
    || (data.is_active ? 'attestation_active' : 'devis_draft');
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.devis_draft;
  const { Icon } = cfg;

  return (
    <div
      data-testid={cfg.testId}
      className={`rounded-2xl border-2 ${cfg.cardBorder} ${cfg.cardBg} p-6 md:p-8 shadow-sm`}
    >
      <div className="flex items-start gap-4">
        <div className={`rounded-full ${cfg.iconBg} p-3 shadow-lg ${cfg.iconShadow}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
        <div className="flex-1">
          <div className={`inline-flex items-center gap-2 rounded-full ${cfg.badgeBg} px-3 py-1 text-xs font-bold text-white uppercase tracking-wider`}>
            {cfg.badge}
          </div>
          <h2 className={`mt-3 text-2xl md:text-3xl font-extrabold ${cfg.headerColor} tracking-tight`}>
            {cfg.title}
          </h2>
          <p className={`mt-2 ${cfg.bodyColor} text-base`}>
            {cfg.description}
            {kind === 'attestation_active' && data.garantie_fin && (
              <> Garantie en vigueur jusqu'au <strong>{formatDate(data.garantie_fin)}</strong>
              {data.garantie_duree_annees ? ` (${data.garantie_duree_annees} ans)` : ''}.</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
          {label}
        </div>
        <div className="text-sm text-gray-900 mt-0.5 break-words" data-testid={testId}>
          {value || '—'}
        </div>
      </div>
    </div>
  );
}

export default function VerifyDomiumPage() {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  // Type de document précis selon le QR code scanné. Le PDF qui contient le QR
  // injecte ?doc=<kind> pour qu'on puisse afficher le libellé exact attendu
  // par le secteur (« CP DOS DOMIUM », « Attestation DOS DOMIUM Dommages à
  // l'ouvrage », etc.). Fallback : on dérive du document_kind retourné par
  // l'API.
  const docKindParam = (searchParams.get('doc') || '').toLowerCase();
  const DOC_KIND_LABELS = {
    attestation: "Attestation nominative DOS DOMIUM Dommages à l'ouvrage",
    attestation_provisoire: "Attestation Provisoire DOS DOMIUM",
    adhesion: "Attestation d'adhésion DOS DOMIUM",
    cp: "Conditions Particulières DOS DOMIUM",
    cg: "Conditions Générales DOS DOMIUM",
    devis: "Devis ESS DOS DOMIUM",
    recu: "Reçu de paiement DOS DOMIUM",
    sinistre: "Déclaration de sinistre DOS DOMIUM",
    annexe_passerelle: "Annexe Passerelle DOS DOMIUM",
  };
  const fallbackLabelFromKind = (kind) => {
    if (kind === 'attestation_active') return "Attestation nominative DOS DOMIUM Dommages à l'ouvrage";
    if (kind === 'devis_draft') return "Devis ESS DOS DOMIUM (précontractuel)";
    if (kind === 'devis_signed_facture') return "Devis ESS DOS DOMIUM — Bon pour accord (vaut facture)";
    if (kind === 'devis_signed_acompte') return "Devis ESS DOS DOMIUM — Bon pour accord (acompte versé)";
    return "Document DOS DOMIUM";
  };

  // Masquer le badge "Made with Emergent" + injecter favicon COPPAM "Bouclier
  // solidaire" + titre de page dynamique sur la page de vérification publique
  // (cohérence visuelle d'un document officiel COPPAM, et meilleure
  // reconnaissance dans les onglets/bookmarks lorsque le souscripteur partage
  // la page à sa banque ou son notaire).
  useEffect(() => {
    // 1) Hide Emergent badge
    const styleEl = document.createElement('style');
    styleEl.id = 'verify-domium-hide-badge';
    styleEl.textContent = '#emergent-badge{display:none !important;visibility:hidden !important;}';
    document.head.appendChild(styleEl);

    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.setProperty('display', 'none', 'important');
        badge.style.setProperty('visibility', 'hidden', 'important');
      }
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    // 2) Snapshot des favicons existants pour restauration au unmount
    const existingFavicons = Array.from(
      document.querySelectorAll(
        "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
      )
    );
    const previousFaviconHTML = existingFavicons.map((el) => ({ el, parent: el.parentNode }));
    existingFavicons.forEach((el) => el.remove());

    // 3) Injection du favicon "Bouclier solidaire COPPAM" — SVG inline crisp
    //    Inspiration : icône ShieldCheck verte + dégradé emerald-700 → teal-700,
    //    cœur "C" stylisé symbolisant COPPAM. Format SVG pour crispness any-size.
    const faviconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#0d9488"/>
      <stop offset="100%" stop-color="#047857"/>
    </linearGradient>
  </defs>
  <path fill="url(#g)" d="M32 4 L56 14 V32 C56 47 44.5 56.5 32 60 C19.5 56.5 8 47 8 32 V14 Z"/>
  <path fill="none" stroke="#ffffff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" d="M21 33 L29 41 L44 24"/>
</svg>`;
    const faviconDataUri = 'data:image/svg+xml;base64,' + btoa(faviconSvg);
    const linkSvg = document.createElement('link');
    linkSvg.id = 'verify-domium-favicon-svg';
    linkSvg.rel = 'icon';
    linkSvg.type = 'image/svg+xml';
    linkSvg.href = faviconDataUri;
    document.head.appendChild(linkSvg);
    const linkApple = document.createElement('link');
    linkApple.id = 'verify-domium-favicon-apple';
    linkApple.rel = 'apple-touch-icon';
    linkApple.href = faviconDataUri;
    document.head.appendChild(linkApple);

    // 4) Titre de page (visible dans l'onglet et au bookmark)
    const previousTitle = document.title;
    document.title = `COPPAM — Vérification ${reference || 'DOS-DOMIUM'}`;

    return () => {
      observer.disconnect();
      const el = document.getElementById('verify-domium-hide-badge');
      if (el) el.remove();
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.removeProperty('display');
        badge.style.removeProperty('visibility');
      }
      // Restore favicons
      document.getElementById('verify-domium-favicon-svg')?.remove();
      document.getElementById('verify-domium-favicon-apple')?.remove();
      previousFaviconHTML.forEach(({ el, parent }) => {
        if (parent) parent.appendChild(el);
      });
      document.title = previousTitle;
    };
  }, [reference]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/domium/verify-public/${encodeURIComponent(reference)}`
        );
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            error: err?.response?.data?.detail || err?.message || 'Erreur',
            data: null,
          });
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reference]);

  // Détermine si on affiche le bloc "signature" et son intitulé selon document_kind
  const docKind = state.data?.document_kind
    || (state.data?.is_active ? 'attestation_active' : 'devis_draft');
  const isAttestationActive = docKind === 'attestation_active';
  const isDevisSigned = docKind === 'devis_signed_facture' || docKind === 'devis_signed_acompte';
  const showSignatureBlock = state.data && (isAttestationActive || isDevisSigned);

  const signatureBlockConfig = isAttestationActive
    ? {
        title: 'Signature officielle de la Présidence COPPAM',
        Icon: ShieldCheck,
        borderColor: 'border-emerald-200',
        bgColor: 'bg-emerald-50/50',
        textColor: 'text-emerald-900',
      }
    : {
        title: 'Bon pour accord du souscripteur',
        Icon: ShieldAlert,
        borderColor: 'border-sky-200',
        bgColor: 'bg-sky-50/50',
        textColor: 'text-sky-900',
      };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center shadow-md">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-extrabold text-gray-900 text-base leading-tight">COPPAM</div>
              <div className="text-xs text-gray-500 leading-tight">Vérification DOS-DOMIUM</div>
            </div>
          </Link>
          <a
            href="https://coppam-garanties-solidaires.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1"
          >
            Site officiel <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {state.loading && (
          <div
            data-testid="verify-loading"
            className="flex flex-col items-center justify-center py-24 text-gray-500"
          >
            <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
            <p className="mt-4 text-sm">Vérification en cours…</p>
          </div>
        )}

        {state.error && !state.loading && (
          <div
            data-testid="verify-error"
            className="rounded-2xl border-2 border-red-300 bg-red-50 p-6"
          >
            <div className="flex items-start gap-4">
              <ShieldX className="h-8 w-8 text-red-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-red-900">Erreur de vérification</h2>
                <p className="mt-2 text-red-800 text-sm">{state.error}</p>
              </div>
            </div>
          </div>
        )}

        {state.data && !state.loading && (
          <div className="space-y-6">
            <StatusBanner data={state.data} />
            {state.data.etat_attestation && (
              <div className={`rounded-xl border-2 p-4 ${state.data.etat_attestation.valide ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-red-300 bg-red-50 text-red-900'}`} data-testid="verify-etat-attestation">
                <p className="text-xs uppercase tracking-wider font-semibold opacity-70">État de paiement de l'attestation</p>
                <p className="text-lg font-extrabold">{state.data.etat_attestation.statut_paiement} — {state.data.etat_attestation.mention}</p>
              </div>
            )}

            {/* Reference + Type de document */}
            <div className="rounded-xl border bg-white p-5 shadow-sm" data-testid="verify-reference-card">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Référence du dossier
                  </div>
                  <div className="font-mono text-xl font-extrabold text-gray-900 tracking-wide" data-testid="verify-reference-value">
                    {state.data.reference}
                  </div>
                </div>
                <div className="sm:text-right">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                    Type de document
                  </div>
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-sm font-bold text-emerald-900"
                    data-testid="verify-type-document"
                  >
                    <FileText className="h-3.5 w-3.5 text-emerald-700" />
                    {DOC_KIND_LABELS[docKindParam] || fallbackLabelFromKind(state.data.document_kind)}
                  </div>
                </div>
              </div>
            </div>

            {state.data.found && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Identité souscripteur */}
                <div className="rounded-xl border bg-white p-5 shadow-sm" data-testid="verify-identite-card">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-emerald-600" />
                    Souscripteur
                  </h3>
                  <div className="space-y-0">
                    <InfoRow icon={Building2} label="Nom / Raison sociale" value={state.data.identite?.nom} testId="verify-identite-nom" />
                    <InfoRow icon={FileText} label="Qualité" value={state.data.identite?.qualite} testId="verify-identite-qualite" />
                    <InfoRow icon={FileText} label="SIRET" value={state.data.identite?.siret_masque} />
                    <InfoRow icon={MapPin} label="Ville" value={state.data.identite?.ville} />
                  </div>
                </div>

                {/* Opération */}
                <div className="rounded-xl border bg-white p-5 shadow-sm" data-testid="verify-operation-card">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-emerald-600" />
                    Opération
                  </h3>
                  <div className="space-y-0">
                    <InfoRow icon={FileText} label="Type" value={state.data.operation?.type_operation} testId="verify-operation-type" />
                    <InfoRow icon={FileText} label="Destination" value={state.data.operation?.destination} />
                    <InfoRow icon={MapPin} label="Ville / Territoire" value={`${state.data.operation?.ville_chantier || '—'}${state.data.operation?.territoire ? ' / ' + state.data.operation.territoire : ''}`} testId="verify-operation-ville" />
                    <InfoRow icon={MapPin} label="Adresse chantier" value={state.data.operation?.adresse_chantier} />
                    <InfoRow icon={FileText} label="Permis de construire" value={state.data.operation?.reference_permis} />
                    <InfoRow icon={FileText} label="Réf. cadastrale" value={state.data.operation?.reference_cadastrale} />
                  </div>
                </div>
              </div>
            )}

            {/* Bloc Signature : libellé adapté au document_kind */}
            {showSignatureBlock && (
              <div
                className={`rounded-xl border-2 ${signatureBlockConfig.borderColor} ${signatureBlockConfig.bgColor} p-5 shadow-sm`}
                data-testid="verify-signature-card"
              >
                <h3 className={`text-sm font-bold ${signatureBlockConfig.textColor} mb-3 flex items-center gap-2`}>
                  <signatureBlockConfig.Icon className="h-4 w-4" />
                  {signatureBlockConfig.title}
                </h3>
                <div className="space-y-0">
                  <InfoRow icon={FileText} label="Signataire" value={state.data.signer_name} testId="verify-signer-name" />
                  <InfoRow icon={Calendar} label="Date de signature" value={formatDate(state.data.signed_at)} testId="verify-signed-at" />
                  <InfoRow icon={FileText} label="Référence signature" value={state.data.signature_reference} />
                  <InfoRow icon={Clock} label="Mode" value={state.data.mode} />
                  {isAttestationActive && (
                    <InfoRow icon={Calendar} label="Fin de garantie" value={formatDate(state.data.garantie_fin)} />
                  )}
                </div>
                {isDevisSigned && (
                  <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 leading-relaxed">
                    <strong>Important :</strong> ce document est un Devis ESS signé pour acceptation par le souscripteur.
                    Il ne s'agit <strong>pas</strong> d'une attestation DOMIUM. La garantie ne devient active qu'après
                    acquittement intégral et contre-signature de la Présidence COPPAM.
                  </p>
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div className="text-xs text-gray-500 text-center pt-6 leading-relaxed">
              Page de vérification publique générée par COPPAM — Garanties Solidaires.
              Les données affichées sont partielles et masquées conformément au RGPD.
              <br />
              <span className="font-mono text-[10px]">Réf : {state.data.reference}</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
